import { NavLink, useLocation } from 'react-router-dom'
import { useState } from 'react'
import {
    LayoutDashboard,
    Globe,
    Box,
    Settings,
    ChevronDown,
    ChevronUp,
    Cog,
    UserCheck,
    Network,
    Microscope,
    ArrowDownToLine,
    Code2,
    ShieldAlert,
    CloudCog,
} from 'lucide-react'
import { useIsAuthenticated, hasRole, useUser } from '@/app/store/auth'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { useAppType } from '@/hooks/useAppType'
import { useQuery } from '@tanstack/react-query'
import { servicenowCmdbApi } from '@/features/servicenow-cmdb/api'


export function Sidebar() {
    const location = useLocation()
    const isAuthenticated = useIsAuthenticated()
    const user = useUser()
    const { t } = useTranslation()
    const { appType, isLoading: appTypeLoading } = useAppType()
    const [adminMenuOpen, setAdminMenuOpen] = useState(true)
    const [recoveryManagerMenuOpen, setRecoveryManagerMenuOpen] = useState(true)
    const [engineersMenuOpen, setEngineersMenuOpen] = useState(true)
    const [isHovered, setIsHovered] = useState(false)

    // Sidebar is always collapsed by default, expands on hover
    const sidebarCollapsed = !isHovered

    // Check for pending CMDB updates (new/changed items)
    const { data: cmdbPendingData } = useQuery({
        queryKey: ['cmdb-pending-count'],
        queryFn: async () => {
            const res = await servicenowCmdbApi.getPendingCount()
            return res.data?.data?.pendingCount ?? 0
        },
        enabled: isAuthenticated && hasRole(user, 'admin'),
        refetchInterval: 60000, // Poll every 60 seconds
    })
    const cmdbHasUpdates = (cmdbPendingData ?? 0) > 0

    if (!isAuthenticated) {
        return null
    }

    // ── OPERATE section ──
    const operateItems = [
        { name: t('navigation.dashboard'), href: '/', icon: LayoutDashboard },
        { name: t('navigation.objects'), href: '/objects', icon: Box },
        { name: t('navigation.planningCanvas'), href: '/visualization', icon: Network },
        { name: t('navigation.ingressMonitoring'), href: '/ingress-monitoring', icon: ArrowDownToLine },
        { name: t('navigation.forensics'), href: '/forensics', icon: Microscope },
        { name: t('navigation.analyticsAnomaly', 'Analytics & Anomaly'), href: '/analytics', icon: ShieldAlert },
        { name: t('navigation.iac', 'IaC'), href: '/iac', icon: Code2 },
        { name: t('navigation.servicenowCmdb', 'ServiceNow CMDB'), href: '/servicenow-cmdb', icon: CloudCog },
    ]

    // ── RECOVER section ──
    const recoverItems = [
        {
            name: t('navigation.recoveryManager'),
            icon: Cog,
            children: [
                { name: t('navigation.recoveryManagement'), href: '/recovery-management' },
                { name: t('navigation.requestApprovals'), href: '/request-approvals' },
                { name: t('navigation.assignEngineer'), href: '/assign-engineer' },
            ],
        },
        {
            name: t('navigation.myAssignments', 'My Assignments'),
            icon: UserCheck,
            children: [
                { name: t('navigation.newAssignments'), href: '/my-assignments' },
                { name: t('navigation.updateRunBook'), href: '/update-runbook' },
            ],
        },
    ]

    // ── ADMIN section ──
    const adminMenuItems = [
        { name: t('navigation.vaults'), href: '/vaults' },
        { name: t('navigation.users'), href: '/users' },
        { name: t('navigation.layers'), href: '/layers' },
        { name: t('navigation.locations'), href: '/locations' },
        { name: t('navigation.roles'), href: '/roles' },
        { name: t('navigation.teams'), href: '/teams' },
        { name: t('navigation.technologies'), href: '/technologies' },
        { name: t('navigation.contacts'), href: '/contacts' },
        { name: t('navigation.masterRunbooks'), href: '/master-runbooks' },
        { name: t('navigation.schedulers', 'Schedulers'), href: '/schedulers' },
        { name: t('navigation.aiModels'), href: '/ai-models' },
        { name: t('navigation.aiKnowledgeBase'), href: '/ai-knowledge-base' },
    ]

    return (
        <div
            className={cn(
                'flex flex-col bg-sidebar border-r border-border transition-all duration-300',
                sidebarCollapsed ? 'w-16' : 'w-64'
            )}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Header */}
            <div className="flex items-center justify-center h-[var(--topbar-height)] border-b border-border">
                {!sidebarCollapsed && (
                    <div className="flex items-center space-x-2 px-4">
                        <Globe className="h-6 w-6 text-primary" />
                        <span className="font-semibold text-lg whitespace-nowrap">
                            {appTypeLoading ? '...' : t(`app.name.${appType}`)}
                        </span>
                    </div>
                )}
                {sidebarCollapsed && (
                    <Globe className="h-6 w-6 text-primary" />
                )}
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                {/* ── OPERATE Section ── */}
                {!sidebarCollapsed && (
                    <div className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {t('navigation.operate', 'OPERATE')}
                    </div>
                )}
                {operateItems.map((item) => {
                    const isActive = location.pathname === item.href
                    const Icon = item.icon
                    const showDot = item.href === '/servicenow-cmdb' && cmdbHasUpdates

                    return (
                        <NavLink
                            key={item.href}
                            to={item.href}
                            onClick={() => setIsHovered(false)}
                            className={cn(
                                'flex items-center space-x-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                                'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                                isActive
                                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                                    : 'text-sidebar-foreground',
                                sidebarCollapsed && 'justify-center'
                            )}
                            title={sidebarCollapsed ? item.name : undefined}
                        >
                            <span className="relative flex-shrink-0">
                                <Icon className="h-5 w-5" />
                                {showDot && (
                                    <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-500" />
                                    </span>
                                )}
                            </span>
                            {!sidebarCollapsed && <span className="whitespace-nowrap">{item.name}</span>}
                        </NavLink>
                    )
                })}

                {/* ── RECOVER Section ── */}
                {(hasRole(user, 'admin') || hasRole(user, 'user')) && (
                    <>
                        {!sidebarCollapsed && (
                            <div className="px-3 pt-4 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                {t('navigation.recover', 'RECOVER')}
                            </div>
                        )}
                        {recoverItems.map((group, groupIdx) => {
                            const Icon = group.icon
                            const isOpen = groupIdx === 0 ? recoveryManagerMenuOpen : engineersMenuOpen
                            const setOpen = groupIdx === 0 ? setRecoveryManagerMenuOpen : setEngineersMenuOpen

                            return (
                                <div key={group.name} className="space-y-0.5">
                                    {!sidebarCollapsed ? (
                                        <>
                                            <button
                                                onClick={() => setOpen(!isOpen)}
                                                className={cn(
                                                    'flex items-center justify-between w-full rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                                                    'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                                                    'text-sidebar-foreground'
                                                )}
                                            >
                                                <div className="flex items-center space-x-3">
                                                    <Icon className="h-5 w-5 flex-shrink-0" />
                                                    <span className="whitespace-nowrap">{group.name}</span>
                                                </div>
                                                {isOpen ? (
                                                    <ChevronUp className="h-4 w-4" />
                                                ) : (
                                                    <ChevronDown className="h-4 w-4" />
                                                )}
                                            </button>
                                            {isOpen && (
                                                <div className="border-l-2 border-gray-200 ml-6 pl-3 space-y-0.5">
                                                    {group.children.map((child) => {
                                                        const isChildActive = location.pathname === child.href
                                                        return (
                                                            <NavLink
                                                                key={child.href}
                                                                to={child.href}
                                                                onClick={() => setIsHovered(false)}
                                                                className={cn(
                                                                    'flex items-center rounded-lg px-3 py-1.5 text-sm transition-colors',
                                                                    'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                                                                    isChildActive
                                                                        ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                                                                        : 'text-sidebar-foreground'
                                                                )}
                                                            >
                                                                <span className="whitespace-nowrap">{child.name}</span>
                                                            </NavLink>
                                                        )
                                                    })}
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div
                                            className="flex items-center justify-center rounded-lg p-2 w-full"
                                            title={group.name}
                                        >
                                            <Icon className="h-5 w-5 text-sidebar-foreground" />
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </>
                )}

                {/* ── ADMIN Section ── */}
                {hasRole(user, 'admin') && (
                    <>
                        {!sidebarCollapsed && (
                            <div className="px-3 pt-4 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                {t('navigation.admin', 'ADMIN')}
                            </div>
                        )}
                        <div className="space-y-0.5">
                            {!sidebarCollapsed ? (
                                <>
                                    <button
                                        onClick={() => setAdminMenuOpen(!adminMenuOpen)}
                                        className={cn(
                                            'flex items-center justify-between w-full rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                                            'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                                            'text-sidebar-foreground'
                                        )}
                                    >
                                        <div className="flex items-center space-x-3">
                                            <Settings className="h-5 w-5 flex-shrink-0" />
                                            <span className="whitespace-nowrap">{t('navigation.admin')}</span>
                                        </div>
                                        {adminMenuOpen ? (
                                            <ChevronUp className="h-4 w-4" />
                                        ) : (
                                            <ChevronDown className="h-4 w-4" />
                                        )}
                                    </button>
                                    {adminMenuOpen && (
                                        <div className="border-l-2 border-gray-200 ml-6 pl-3 space-y-0.5">
                                            {adminMenuItems.map((item) => {
                                                const isActive = location.pathname === item.href
                                                const showDot = item.href === '/servicenow-cmdb' && cmdbHasUpdates

                                                return (
                                                    <NavLink
                                                        key={item.href}
                                                        to={item.href}
                                                        onClick={() => setIsHovered(false)}
                                                        className={cn(
                                                            'flex items-center rounded-lg px-3 py-1.5 text-sm transition-colors',
                                                            'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                                                            isActive
                                                                ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                                                                : 'text-sidebar-foreground'
                                                        )}
                                                    >
                                                        <span className="relative whitespace-nowrap">
                                                            {item.name}
                                                            {showDot && (
                                                                <span className="absolute -top-1 -right-3 flex h-2.5 w-2.5">
                                                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                                                                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-500" />
                                                                </span>
                                                            )}
                                                        </span>
                                                    </NavLink>
                                                )
                                            })}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div
                                    className="flex items-center justify-center rounded-lg p-2 w-full"
                                    title={t('navigation.admin')}
                                >
                                    <Settings className="h-5 w-5 text-sidebar-foreground" />
                                </div>
                            )}
                        </div>

                        {/* Settings */}
                        <NavLink
                            to="/settings"
                            onClick={() => setIsHovered(false)}
                            className={cn(
                                'flex items-center space-x-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                                'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                                location.pathname === '/settings'
                                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                                    : 'text-sidebar-foreground',
                                sidebarCollapsed && 'justify-center'
                            )}
                            title={sidebarCollapsed ? t('navigation.settings') : undefined}
                        >
                            <Settings className="h-5 w-5 flex-shrink-0" />
                            {!sidebarCollapsed && <span className="whitespace-nowrap">{t('navigation.settings')}</span>}
                        </NavLink>
                    </>
                )}
            </nav>

            {/* Footer */}
            {!sidebarCollapsed && (
                <div className="p-4 border-t border-border">
                    <div className="text-xs text-muted-foreground whitespace-nowrap">
                        <div>Environment: {import.meta.env.VITE_ENVIRONMENT || 'dev'}</div>
                        <div>Version: 1.0.0</div>
                    </div>
                </div>
            )}
        </div>
    )
}
